import {
    Alignment, DOMUtil, LocalUserSettings, LogManager, ObjectUtil, StringUtil, UserLayoutConfigManager, UserSettings
} from "@mcleod/core";
import { Component } from "./Component";

const log = LogManager.getLogger("components.base.ResizeManager");

interface ResizeProps<T extends Component> {
    component: T;
    /** Informs resize logic if width is increased or decreased during drag action. */
    dragEdge: Alignment.LEFT | Alignment.RIGHT;
    /** A minimum width used if the component does not have a minWidth set. Defaulted to `50` if not provided. */
    defaultMinWidth?: number;
}

export interface ResizeStorageProps {
    /** The key to be used to save resize adjustments in local storage. */
    localSettingsKey?: string;
    /** A function that returns the values to be saved to local storage. */
    currentUserSettings?: (() => any);
    /** A function to use to apply setting properties to component. */
    applyUserSettings?: ((positionSettings: any) => void);
    /** A function to return a standardized key to be used in local storage. */
    componentSettingsKey?: (() => string);
    /** Set to true to bypass the global user_save_screen_sizes check. */
    ignoreSaveScreenSizeSetting?: boolean;
}

export abstract class ResizeManager<T extends Component> {
    protected readonly component: T;
    protected readonly dragEdge: Alignment.RIGHT | Alignment.LEFT;
    private readonly defaultMinimumWidth: number;
    private _internalStorageProps: ResizeStorageProps;

    private _resizeDiv: HTMLDivElement;
    private cursorProps: Partial<CSSStyleDeclaration>;
    protected resizeStartX: number;
    protected resizeStartWidth: number;
    private _originalComponentWidth: number;
    private _componentResizeSettings: ComponentResizeSettings;

    protected constructor(props: ResizeProps<T>) {
        this.component = props.component;
        this.dragEdge = props.dragEdge;
        this.defaultMinimumWidth = props.defaultMinWidth || 50;
        this.component.addMountListener(() => this.sync());
    }

    protected abstract createResizeDiv(): HTMLDivElement;

    protected get resizeDiv(): HTMLDivElement {
        if (this._resizeDiv == null) {
            this._resizeDiv = this.createResizeDiv();
            this._resizeDiv.addEventListener('mousedown', this.startResizing.bind(this));
        }
        return this._resizeDiv;
    }

    private addResizeDiv() {
        if (!this.component._element.contains(this.resizeDiv)) {
            this.component._element.prepend(this.resizeDiv);
        }
    }

    private removeResizeDiv() {
        if (this.component._element.contains(this.resizeDiv)) {
            this.component._element.removeChild(this.resizeDiv);
        }
    }

    sync() {
        this.removeResizeDiv();
        if (this.shouldSyncResizer) {
            this.addResizeDiv();
            this.setupUserWidthPreferences();
        }
    }

    protected get shouldSyncResizer(): boolean {
        return true;
    }

    get canResize(): boolean {
        return this.component._element.contains(this.resizeDiv);
    }

    get isResizing(): boolean {
        return this.resizeStartX != null && this.resizeStartWidth != null;
    }

    private startResizing(e: MouseEvent) {
        e.preventDefault();
        this.resizeStartX = e.clientX;
        this.resizeStartWidth = this.componentWidth;
        this.setTemporaryStyles();
        this.onResizeStart(e);

        window.addEventListener('mousemove', this.performResizing.bind(this));
        window.addEventListener('mouseup', this.stopResizing.bind(this));
    }

    /** Override to perform logic when resizing has started. */
    protected onResizeStart(_e: MouseEvent) {}

    private performResizing(e: MouseEvent) {
        if (!this.isResizing) return;

        const delta = e.pageX - this.resizeStartX;
        let newWidth = this.dragEdge == Alignment.LEFT ? this.resizeStartWidth - delta : this.resizeStartWidth + delta;
        newWidth = Math.max(this.componentMinWidth, Math.min(newWidth, this.componentMaxWidth));
        if (newWidth != this.resizeStartWidth) {
            this.onResizeMove(e, newWidth);
        }
    }

    /**
     * Override to perform some logic when resizing is underway.
     * </br>
     * NOTE: Overriding this will require you to set the new width
     * value through super call or your own logic.
     */
    protected onResizeMove(_e: MouseEvent, newWidth: number) {
        this.component.width = newWidth;
    }

    private stopResizing(e: MouseEvent) {
        if (!this.isResizing) return;

        this.revertTemporaryStyles();
        this.onResizeEnd(e);
        this.resizeStartX = null;
        this.resizeStartWidth = null;

        this.savePositions();
        window.removeEventListener('mousemove', this.performResizing, false);
        window.removeEventListener('mouseup', this.stopResizing, false);
    }

    /** Override to perform logic once resizing has stopped. */
    protected onResizeEnd(_e: MouseEvent) {}

    private setTemporaryStyles() {
        this.cursorProps = {
            cursor: document.body.style.cursor,
            userSelect: this.component._element.style.userSelect
        };
        document.body.style.cursor = this.resizeDiv.style.cursor;
        this.component.style.userSelect = "none";
    }

    private revertTemporaryStyles() {
        if (this.cursorProps) {
            document.body.style.cursor = this.cursorProps.cursor;
            this.component._element.style.userSelect = this.cursorProps.userSelect;
        }
    }

    protected get componentWidth(): number {
        return this.actualWidth(this.component);
    }

    protected get originalComponentWidth(): number {
        return this._originalComponentWidth;
    }

    protected actualWidth(comp: T): number {
        return DOMUtil.getElementWidth(comp._element);
    }

    protected get componentMaxWidth(): number {
        return this.actualMaxWidth(this.component);
    }

    protected actualMaxWidth(comp: T): number {
        return comp.maxWidth ? this.asNumber(comp.maxWidth) : Number.MAX_SAFE_INTEGER
    }

    protected get componentMinWidth(): number {
        return this.actualMinWidth(this.component);
    }

    protected actualMinWidth(comp: T): number {
        const minimumWidth: number = this.asNumber(comp.minWidth);

        if (minimumWidth)
            return minimumWidth;
        else if (this.originalComponentWidth)
            return Math.min(this.originalComponentWidth, this.defaultMinimumWidth);
        return this.defaultMinimumWidth;
    }

    private asNumber(value: string | number): number {
        if (value == null) return 0;

        if (typeof value === 'number') {
            return value;
        } else {
            const trimmed = value.trim();
            if (trimmed.endsWith('%')) {
                // Convert percentage to pixels based on containingElement's width
                const percent = parseFloat(trimmed.slice(0, -1));
                if (isNaN(percent) || !this.containingElement) return 0;
                const containerWidth = DOMUtil.getElementWidth(this.containingElement);
                return (percent / 100) * containerWidth;
            } else {
                return DOMUtil.getStyleAttrAsNumber(trimmed) || 0;
            }
        }
    }

    protected abstract get containingElement(): HTMLElement;

    private get useSavedPositions(): boolean {
        const isSaveAllowed = this.internalStorageProps.ignoreSaveScreenSizeSetting
                           || UserSettings.get().user_save_screen_sizes === true;

        return isSaveAllowed
            && StringUtil.isEmptyString(this.localSettingsKey) !== true
            && this.applyUserSettings != null
            && this.currentUserSettings != null;
    }

    private get componentResizeSettings(): ComponentResizeSettings | null {
        if (this._componentResizeSettings == null)
            this._componentResizeSettings = new ComponentResizeSettings(this.localSettingsKey);
        return this._componentResizeSettings;
    }

    protected setupUserWidthPreferences() {
        if (!this.useSavedPositions) return;

        this._originalComponentWidth ??= this.componentWidth;
        const settings = this.componentResizeSettings;
        if (settings == null) return;

        const componentSettingsKey = this.componentSettingsKey;
        if (componentSettingsKey == null) return;

        const positionsObject = settings.getComponentSettings(componentSettingsKey);
        if (positionsObject == null) return;

        this.applyUserSettings(positionsObject);
    }

    /**
     * Override this getter to supply required information and functions for how to store and set the data.
     * Doing this is not required, unless you'd like to save the data to local storage.
     */
    protected get storageProps(): ResizeStorageProps {
        return null;
    }

    private get internalStorageProps(): ResizeStorageProps {
        if (this._internalStorageProps == null)
            this._internalStorageProps = this.storageProps || {};
        return this._internalStorageProps;
    }

    private get localSettingsKey(): string {
        return this.internalStorageProps.localSettingsKey;
    }

    private get currentUserSettings(): (() => any) {
        return this.internalStorageProps.currentUserSettings;
    }

    private get applyUserSettings(): ((settings: any) => void) {
        return this.internalStorageProps.applyUserSettings;
    }

    private get componentSettingsKey(): string {
        return this.internalStorageProps.componentSettingsKey?.();
    }

    private savePositions() {
        if (!this.useSavedPositions) return;

        const componentSettingsKey = this.componentSettingsKey;
        if (StringUtil.isEmptyString(componentSettingsKey)) return;

        const positions = this.currentUserSettings();
        if (ObjectUtil.isEmptyObject(positions))
            this.componentResizeSettings.removeComponentSettings(componentSettingsKey);
        else
            this.componentResizeSettings.saveComponentSettings(componentSettingsKey, positions);
    }
}

class ComponentResizeSettings {
    localStorageSettingsKey: string;

    constructor(localStorageSettingsKey: string) {
        this.localStorageSettingsKey = localStorageSettingsKey;
    }

    get allSettings(): Record<string, object> {
        return LocalUserSettings.getObject(this.localStorageSettingsKey, {}) as Record<string, any>;
    }

    getComponentSettings(componentSettingsKey: string): any {
        if (componentSettingsKey) {
            return this.allSettings[componentSettingsKey] || null;
        }
        return null;
    }

    saveComponentSettings(componentSettingsKey: string, settings: any) {
        if (componentSettingsKey) {
            UserLayoutConfigManager.save(this.localStorageSettingsKey, componentSettingsKey, settings);
        } else {
            log.debug("Could not determine unique identifier for component.");
        }
    }

    removeComponentSettings(componentSettingsKey: string): void {
        UserLayoutConfigManager.delete(this.localStorageSettingsKey, componentSettingsKey);
    }
}
